import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/services/api/client'
import { WaitingPoolMatrix } from '@/components/admin/WaitingPoolMatrix'
import { RefreshCw, Clock, Users, AlertTriangle } from 'lucide-react'
import { useAdminDashboardConfig } from '@/hooks/useAdminDashboardConfig'
import { useToast } from '@/components/ui/ToastProvider'

interface WaitingPoolRequestSummary {
  id: string
  request_id: string
  date: string
  subject_name: string
  klass_name: string
  requesting_teacher_name: string
  teacher_name: string
  lesson_count: number
  waiting_teacher_count: number
  deadline: string | null
  status: string
}

type PoolState = 'active' | 'expired'

const DEFAULT_EXTENSION_OPTIONS: number[] = [1, 2, 4, 24]

export function WaitingPoolAdminPage() {
  const { showToast } = useToast()
  const [requests, setRequests] = useState<WaitingPoolRequestSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [poolState, setPoolState] = useState<PoolState>('active')
  const { data: config } = useAdminDashboardConfig()
  const subLimit = config?.admin_list_limit_substitutions ?? 50

  // Deadline Extension State
  const [showExtendModal, setShowExtendModal] = useState(false)
  const [extendingRequestId, setExtendingRequestId] = useState<string | null>(null)
  const [extendHours, setExtendHours] = useState<number | null>(null)
  const [notifyTeachers, setNotifyTeachers] = useState(false)
  const [extendMessage, setExtendMessage] = useState('')
  const [extending, setExtending] = useState(false)
  const [extensionOptions, setExtensionOptions] = useState<number[]>(DEFAULT_EXTENSION_OPTIONS)

  const fetchRequests = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = {
        skip: 0,
        limit: subLimit,
        waiting_pool_state: poolState,
      }
      const statsRes = await apiClient.get('/api/v1/admin/substitutions', { params })
      const reqs = statsRes.data?.substitutions || []
      setRequests(reqs)
      setError(null)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Daten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [subLimit, poolState])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  // Load extension options from admin waiting-pool stats endpoint
  // (the unified config endpoint is superadmin-only; admins get the
  // configured quick-options via waiting-pool-stats.extension_options)
  useEffect(() => {
    apiClient.get('/api/v1/admin/waiting-pool-stats')
      .then(res => {
        // Coerce: JSON round-trips may deliver strings; validate 1..168h
        const raw = res.data?.extension_options
        const opts = (Array.isArray(raw) ? raw : [])
          .map(Number)
          .filter(n => Number.isInteger(n) && n >= 1 && n <= 168)
        if (opts.length > 0) {
          setExtensionOptions(opts)
        }
      })
      .catch(() => {
        setExtensionOptions(DEFAULT_EXTENSION_OPTIONS)
      })
  }, [])

  const closeExtendModal = () => {
    setShowExtendModal(false)
    setExtendingRequestId(null)
    setExtendHours(null)
    setNotifyTeachers(false)
    setExtendMessage('')
  }

  const handleExtend = async () => {
    if (!extendingRequestId || !extendHours) return
    setExtending(true)
    try {
      const res = await apiClient.post(
        `/api/v1/admin/substitutions/${extendingRequestId}/extend-deadline`,
        {
          hours: extendHours,
          notify_teachers: notifyTeachers,
          message: extendMessage || null,
        }
      )
      const notified = res.data?.notified_count ?? 0
      showToast({
        message: notified > 0
          ? `Deadline verlängert — ${notified} Lehrer benachrichtigt`
          : 'Deadline verlängert',
        type: 'success',
        duration: 3000,
      })
      closeExtendModal()
      fetchRequests()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      showToast({
        message: detail || 'Fehler beim Verlängern der Deadline',
        type: 'error',
        duration: 5000,
      })
    } finally {
      setExtending(false)
    }
  }

  // Modal: Escape to close
  useEffect(() => {
    if (!showExtendModal) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeExtendModal()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showExtendModal])

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Waiting Pool Verwaltung</h1>
          <p className="text-sm text-gray-500 mt-1">
            Uebersicht aller Anfragen mit aktiven Waiting-Pools und manuelle Zuweisung
          </p>
        </div>
        <button
          onClick={fetchRequests}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition disabled:opacity-50"
          title="Aktualisieren"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Pool state toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setPoolState('active')}
          className={`px-4 py-1.5 text-sm rounded-lg border transition-colors ${
            poolState === 'active'
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
          }`}
        >
          Aktive Pools
        </button>
        <button
          onClick={() => setPoolState('expired')}
          className={`px-4 py-1.5 text-sm rounded-lg border transition-colors ${
            poolState === 'expired'
              ? 'bg-amber-500 text-white border-amber-500'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
          }`}
        >
          Abgelaufen &amp; unauflöst
        </button>
        {poolState === 'expired' && (
          <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Deadline abgelaufen, Auflösung durch Job noch ausstehend — Verlängerung reaktiviert den Pool
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-500">Lade Daten...</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {poolState === 'active' ? 'Keine aktiven Waiting-Pools' : 'Keine abgelaufenen, unauflösten Waiting-Pools'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Request list */}
          <div className="space-y-3">
            {requests.map((req) => (
              <div
                key={req.request_id || req.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedRequestId(req.request_id || req.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedRequestId(req.request_id || req.id)
                  }
                }}
                className={`w-full text-left p-4 rounded-xl border transition-colors cursor-pointer ${
                  selectedRequestId === (req.request_id || req.id)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {req.subject_name || '—'} · {req.klass_name || '—'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                    {req.status}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {req.waiting_teacher_count ?? '?'} Lehrer im Pool
                  </span>
                  <span>{req.lesson_count ?? '?'} Lektionen</span>
                  {req.deadline && (
                    <span
                      className={`flex items-center gap-1 ${
                        new Date(req.deadline) < new Date()
                          ? 'text-red-600 dark:text-red-400 line-through'
                          : 'text-amber-600'
                      }`}
                    >
                      <Clock className="w-3 h-3" />
                      {new Date(req.deadline).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  von {req.requesting_teacher_name || '—'}
                </div>
                {req.deadline && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setExtendingRequestId(req.request_id || req.id)
                      setShowExtendModal(true)
                    }}
                    className="mt-2 px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Deadline verlängern
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Detail: Waiting Pool Matrix */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            {selectedRequestId ? (
              <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <WaitingPoolMatrix requestId={selectedRequestId} />
              </div>
            ) : (
              <div className="p-8 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-center">
                <AlertTriangle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  Wählen Sie eine Anfrage links aus, um das Waiting-Pool-Assembly zu sehen.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deadline Extension Modal */}
      {showExtendModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Deadline verlängern"
          onClick={closeExtendModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Deadline verlängern</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Verlängerung um:</label>
              <div className="flex gap-2 flex-wrap">
                {extensionOptions.map(hours => (
                  <button
                    key={hours}
                    onClick={() => setExtendHours(hours)}
                    className={`px-4 py-2 rounded-lg border ${
                      extendHours === hours
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    }`}
                  >
                    +{hours}h
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={notifyTeachers}
                  onChange={(e) => setNotifyTeachers(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Lehrer im Waiting Pool benachrichtigen</span>
              </label>
            </div>

            {notifyTeachers && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Persönliche Nachricht (optional):</label>
                <textarea
                  value={extendMessage}
                  onChange={(e) => setExtendMessage(e.target.value)}
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  rows={3}
                  maxLength={500}
                />
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={closeExtendModal}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Abbrechen
              </button>
              <button
                onClick={handleExtend}
                disabled={!extendHours || extending}
                className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {extending ? 'Verlängere...' : 'Verlängern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
